import { useShellSearchParams } from '../lib/use-shell-search-params';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useLocale } from '../i18n/locale-context';
import { useSearchFacets } from '../lib/use-search-facets';
import { countryConfig } from '../country';
import { catalogSectionServed, type PublicCountryConfig } from '@tenders/shared';

type Scope = 'all' | 'ifi' | 'supply';

const CHIPS: Array<{ scope: Scope; to: string; labelKey: string }> = [
  { scope: 'all', to: '/catalog', labelKey: 'shell.nav.allTenders' },
  { scope: 'ifi', to: '/ifi', labelKey: 'shell.nav.ifiProjects' },
  { scope: 'supply', to: '/supply', labelKey: 'shell.nav.supplyRequests' },
];

/**
 * The chips this deployment can fill. «Кооперация» is cooperation.uz supply
 * requests: a deployment with no supply-scope source (KZ) drops the chip from
 * its CONFIG, not from a zero count — a zero is a real answer on UZ («this
 * query has no supply requests»), but on KZ it could never be anything else
 * (phone QA, 2026-10-16: «Все тендеры 149 · Проекты МФИ 3 · Кооперация 0»).
 */
export function visibleScopeChips(
  config: Pick<PublicCountryConfig, 'features'> = countryConfig,
): typeof CHIPS {
  const chips = config.features.supplyScope ? CHIPS : CHIPS.filter((c) => c.scope !== 'supply');
  // A section the country does not serve at all (`features.sections`, Spain)
  // has no chip either. Absent on UZ/KZ, so their list is the line above.
  return chips.filter((c) => c.scope === 'all' || catalogSectionServed(config, c.scope));
}

/**
 * Scope filter chips above the search results — «Все тендеры · Проекты МФИ ·
 * Запросы предложений» with the QUERY's per-layer counts, each a link that
 * re-scopes while carrying the query (`/ifi?q=…`). The catalog default browses
 * gov+corporate only, so a search is where a lot's layer actually varies; this
 * is the surface to see and act on that split.
 *
 * Only rendered while a search is active (the facet hook is disabled otherwise
 * and the counts have no meaning off a query — browse scope is the sidebar's
 * job). Counts share ONE request with the kind tabs via `useSearchFacets`.
 * A chip whose layer has zero matches is dimmed but kept, so the row's shape is
 * stable and «this query has no МФИ lots» is itself an answer.
 */
export function ScopeChips({
  activeScope,
  didYouMean,
}: {
  activeScope: Scope;
  /** The API's respelling of a query that matched no lot (`meta.didYouMean`). */
  didYouMean?: string;
}) {
  const { t } = useLocale();
  const [params] = useShellSearchParams();
  const { facets, active } = useSearchFacets();
  const q = params.get('q')?.trim() ?? '';
  if (!active) return null;

  /*
   * «Все тендеры» counts what its list SHOWS — every layer.
   *
   * Igor's phone run, 17.10 (uz): «кампютер» listed 9 lots while this chip
   * read «Barcha tenderlar 0». Both were "right" by their own definition: the
   * list of a home search goes out with `scope=all` (every layer — МФИ since
   * 2026-08-13, and the supply layer since it was ingested on 2026-08-18), and
   * this chip's link `/catalog?q=…` opens that same list; but the count was
   * `facets.scope.all`, which the API defines as gov + corporate only. With
   * the main layer empty and nine cooperation.uz requests found by the
   * semantic arm, the active chip said 0 above nine rows.
   *
   * `facets.kind.all` is the grand total of the same request — the number the
   * «Hammasi» tab beside it already shows — so the chip now agrees with the
   * list and with the tab. What the catalogue covers is unchanged.
   */
  const countFor = (scope: Scope): number | undefined =>
    scope === 'all' ? facets?.kind.all : facets?.scope[scope];
  // The search found nothing in the budgeted main layer (gov + corporate) and
  // everything shown comes from another section — say so above the rows, or a
  // list of budgetless cooperation requests reads as «the tenders for my word».
  const onlyOtherSections =
    activeScope === 'all' && facets != null && facets.scope.all === 0 && facets.kind.all > 0;
  // «Возможно, вы имели в виду: компьютер» — ONLY while the main layer is
  // empty (owner's rule for typos, 17.10): a link to the respelled search,
  // never a swap of the rows below. Off inside a section the reader chose.
  const suggestion =
    activeScope === 'all' && facets != null && facets.scope.all === 0 && didYouMean
      ? didYouMean
      : null;

  const chips = visibleScopeChips();

  return (
    <>
      {/* One chip is no choice: where «all» is the only section (Spain) the row
          is not drawn. UZ (three) and KZ (two) always draw it. */}
      {chips.length > 1 && (
        <nav
          aria-label={t('scope.chips.aria')}
          className="-mx-1 my-3 flex items-center gap-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {chips.map(({ scope, to, labelKey }) => {
            const count = countFor(scope);
            const isActive = scope === activeScope;
            const empty = count === 0;
            return (
              <Link
                key={scope}
                to={`${to}?q=${encodeURIComponent(q)}`}
                aria-current={isActive ? 'page' : undefined}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-pill border px-3 py-1 text-[12.5px] font-semibold transition-colors ${
                  isActive
                    ? 'border-brand/40 bg-brand-dim text-brand'
                    : `border-edge bg-bone-surface text-ink-soft hover:border-brand/30 hover:text-ink ${
                        empty ? 'opacity-55' : ''
                      }`
                }`}
              >
                <span>{t(labelKey)}</span>
                {count != null && (
                  <span
                    className={`min-w-[18px] rounded-pill px-1.5 text-center text-[11px] font-bold leading-[1.5] ${
                      isActive ? 'bg-brand/15 text-brand' : 'bg-bone text-ink-muted'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      )}
      {suggestion && (
        <p data-testid="scope-did-you-mean" className="-mt-1 mb-2 text-[13px] text-ink">
          {t('scope.chips.didYouMean')}{' '}
          <Link
            to={`/catalog?q=${encodeURIComponent(suggestion)}`}
            className="font-semibold text-brand underline"
          >
            {suggestion}
          </Link>
        </p>
      )}
      {onlyOtherSections && (
        <p data-testid="scope-only-other" className="-mt-1 mb-3 text-[12.5px] text-ink-soft">
          {t('scope.chips.onlyOther')}
        </p>
      )}
    </>
  );
}
